// ─────────────────────────────────────────────────────────────────────────────
// AXIS IQ — CORPORATE COGNITIVE ABILITY ASSESSMENT
// 60 questions × 4 sections × 15 questions each
// Difficulty: graduated 1-5 within each section
// ─────────────────────────────────────────────────────────────────────────────

export const IQ_SECTIONS = [
  { id:"verbal",    label:"Verbal Reasoning",    icon:"🔤", color:"#0891b2", short:"VR" },
  { id:"numerical", label:"Numerical Reasoning", icon:"🔢", color:"#0f766e", short:"NR" },
  { id:"abstract",  label:"Abstract Reasoning",  icon:"🔷", color:"#7c3aed", short:"AR" },
  { id:"logical",   label:"Logical Reasoning",   icon:"⚙",  color:"#c8964e", short:"LR" },
];

export const IQ_QUESTIONS = [

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 1: VERBAL REASONING (VR1–VR15)
  // Word analogies, antonyms, vocabulary, sentence completion, reading
  // ════════════════════════════════════════════════════════════════════════════

  // Difficulty 1 — Basic analogies
  {
    id:"VR1", section:"verbal", difficulty:1,
    type:"analogy",
    question:"ARCHITECT is to BUILDING as COMPOSER is to:",
    options:["Concert","Symphony","Musician","Instrument"],
    answer:"Symphony",
    explanation:"An architect creates a building; a composer creates a symphony.",
  },
  {
    id:"VR2", section:"verbal", difficulty:1,
    type:"analogy",
    question:"LIBRARY is to BOOKS as GALLERY is to:",
    options:["Artists","Paintings","Museums","Exhibits"],
    answer:"Paintings",
    explanation:"A library houses books; a gallery houses paintings.",
  },
  {
    id:"VR3", section:"verbal", difficulty:1,
    type:"antonym",
    question:"Choose the word most OPPOSITE in meaning to: TRANSPARENT",
    options:["Clear","Opaque","Visible","Translucent"],
    answer:"Opaque",
    explanation:"Transparent means see-through; opaque means not able to be seen through.",
  },

  // Difficulty 2 — Intermediate vocabulary
  {
    id:"VR4", section:"verbal", difficulty:2,
    type:"synonym",
    question:"Choose the word most SIMILAR in meaning to: AMBIGUOUS",
    options:["Clear","Certain","Equivocal","Definite"],
    answer:"Equivocal",
    explanation:"Both ambiguous and equivocal mean open to more than one interpretation.",
  },
  {
    id:"VR5", section:"verbal", difficulty:2,
    type:"analogy",
    question:"PEDESTRIAN is to WALK as EQUESTRIAN is to:",
    options:["Race","Swim","Ride","Jump"],
    answer:"Ride",
    explanation:"A pedestrian travels on foot; an equestrian travels on horseback.",
  },
  {
    id:"VR6", section:"verbal", difficulty:2,
    type:"odd_one_out",
    question:"Which word does NOT belong with the others?",
    options:["Simile","Metaphor","Allegory","Syntax"],
    answer:"Syntax",
    explanation:"Simile, metaphor and allegory are all figures of speech; syntax is sentence structure.",
  },

  // Difficulty 3 — Sentence completion & comprehension
  {
    id:"VR7", section:"verbal", difficulty:3,
    type:"completion",
    question:"The CEO's decision was _____ by the board, who felt it was both premature and ill-considered.",
    options:["Endorsed","Praised","Censured","Welcomed"],
    answer:"Censured",
    explanation:"Censured means formally criticised or condemned, fitting the context of disapproval.",
  },
  {
    id:"VR8", section:"verbal", difficulty:3,
    type:"analogy",
    question:"SYCOPHANT is to FLATTER as ICONOCLAST is to:",
    options:["Worship","Challenge","Follow","Admire"],
    answer:"Challenge",
    explanation:"A sycophant flatters; an iconoclast challenges established beliefs or institutions.",
  },
  {
    id:"VR9", section:"verbal", difficulty:3,
    type:"antonym",
    question:"Choose the word most OPPOSITE in meaning to: LACONIC",
    options:["Brief","Wordy","Terse","Concise"],
    answer:"Wordy",
    explanation:"Laconic means using very few words; its opposite is wordy or verbose.",
  },

  // Difficulty 4 — Advanced reasoning
  {
    id:"VR10", section:"verbal", difficulty:4,
    type:"passage",
    question:"'All effective leaders demonstrate empathy. Some managers are effective leaders. Therefore:' Which conclusion is definitely true?",
    options:[
      "All managers demonstrate empathy",
      "Some managers demonstrate empathy",
      "No managers demonstrate empathy",
      "All empathetic people are managers",
    ],
    answer:"Some managers demonstrate empathy",
    explanation:"If some managers are effective leaders, and all effective leaders show empathy, then those managers must also show empathy.",
  },
  {
    id:"VR11", section:"verbal", difficulty:4,
    type:"analogy",
    question:"EPHEMERAL is to PERMANENCE as PENURY is to:",
    options:["Poverty","Wealth","Misery","Hardship"],
    answer:"Wealth",
    explanation:"Ephemeral is the opposite of permanence; penury (extreme poverty) is the opposite of wealth.",
  },
  {
    id:"VR12", section:"verbal", difficulty:4,
    type:"odd_one_out",
    question:"Which word does NOT belong with the others?",
    options:["Enervate","Invigorate","Vitalize","Energize"],
    answer:"Enervate",
    explanation:"Invigorate, vitalize and energize all mean to give energy; enervate means to drain energy.",
  },

  // Difficulty 5 — Expert level
  {
    id:"VR13", section:"verbal", difficulty:5,
    type:"analogy",
    question:"SOLIPSISM is to SELF as ANTHROPOCENTRISM is to:",
    options:["Nature","Humanity","Universe","Society"],
    answer:"Humanity",
    explanation:"Solipsism centres everything on the self; anthropocentrism centres everything on humanity.",
  },
  {
    id:"VR14", section:"verbal", difficulty:5,
    type:"completion",
    question:"The philosopher's argument, though ostensibly _____, contained a fundamental _____ that undermined his entire thesis.",
    options:["Coherent / Flaw","Flawed / Logic","Illogical / Truth","Weak / Strength"],
    answer:"Coherent / Flaw",
    explanation:"The sentence contrasts the apparent soundness of the argument with an underlying fatal weakness.",
  },
  {
    id:"VR15", section:"verbal", difficulty:5,
    type:"passage",
    question:"'No innovation succeeds without risk. Some projects avoid all risk. Some projects succeed.' Which is most consistent with the passage?",
    options:[
      "Risk-taking guarantees success",
      "Some successful projects took risks",
      "Projects that avoid risk always fail",
      "All successful projects took risks",
    ],
    answer:"Some successful projects took risks",
    explanation:"The passage says innovation requires risk, and some projects succeed, so those that succeed did take risks. But not ALL projects that take risks succeed.",
  },

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 2: NUMERICAL REASONING (NR1–NR15)
  // Number series, arithmetic, ratios, data interpretation, word problems
  // ════════════════════════════════════════════════════════════════════════════

  // Difficulty 1
  {
    id:"NR1", section:"numerical", difficulty:1,
    type:"series",
    question:"What is the next number in the series?\n2, 4, 8, 16, 32, ___",
    options:["48","56","64","72"],
    answer:"64",
    explanation:"Each number is multiplied by 2. 32 × 2 = 64.",
  },
  {
    id:"NR2", section:"numerical", difficulty:1,
    type:"arithmetic",
    question:"A manager earns $4,800 per month. What is their annual salary?",
    options:["$48,000","$52,800","$57,600","$60,000"],
    answer:"$57,600",
    explanation:"$4,800 × 12 months = $57,600.",
  },
  {
    id:"NR3", section:"numerical", difficulty:1,
    type:"percentage",
    question:"A project budget of $50,000 is 40% spent. How much remains?",
    options:["$20,000","$25,000","$30,000","$35,000"],
    answer:"$30,000",
    explanation:"40% of $50,000 = $20,000 spent. $50,000 – $20,000 = $30,000 remaining.",
  },

  // Difficulty 2
  {
    id:"NR4", section:"numerical", difficulty:2,
    type:"series",
    question:"What is the next number in the series?\n1, 4, 9, 16, 25, ___",
    options:["30","34","36","40"],
    answer:"36",
    explanation:"These are perfect squares: 1², 2², 3², 4², 5², 6² = 36.",
  },
  {
    id:"NR5", section:"numerical", difficulty:2,
    type:"ratio",
    question:"A team has 12 developers and 4 testers. If the team grows to 30 developers, how many testers are needed to maintain the same ratio?",
    options:["8","10","12","15"],
    answer:"10",
    explanation:"Ratio is 12:4 = 3:1. For 30 developers: 30 ÷ 3 = 10 testers.",
  },
  {
    id:"NR6", section:"numerical", difficulty:2,
    type:"word_problem",
    question:"Sales grew from $120,000 to $156,000. What is the percentage increase?",
    options:["25%","30%","33%","36%"],
    answer:"30%",
    explanation:"Increase = $36,000. ($36,000 ÷ $120,000) × 100 = 30%.",
  },

  // Difficulty 3
  {
    id:"NR7", section:"numerical", difficulty:3,
    type:"series",
    question:"What is the next number in the series?\n3, 7, 13, 21, 31, ___",
    options:["41","43","45","47"],
    answer:"43",
    explanation:"Differences are +4, +6, +8, +10, +12. 31 + 12 = 43.",
  },
  {
    id:"NR8", section:"numerical", difficulty:3,
    type:"data",
    question:"A company's Q1–Q4 revenues are $2.1M, $2.4M, $1.9M and $2.8M. What is the average quarterly revenue?",
    options:["$2.25M","$2.30M","$2.35M","$2.40M"],
    answer:"$2.30M",
    explanation:"Total = $9.2M. Average = $9.2M ÷ 4 = $2.30M.",
  },
  {
    id:"NR9", section:"numerical", difficulty:3,
    type:"word_problem",
    question:"Three partners share profits in ratio 2:3:5. The smallest share is $14,000. What is the total profit?",
    options:["$56,000","$63,000","$70,000","$84,000"],
    answer:"$70,000",
    explanation:"2 parts = $14,000, so 1 part = $7,000. Total = 10 parts × $7,000 = $70,000.",
  },

  // Difficulty 4
  {
    id:"NR10", section:"numerical", difficulty:4,
    type:"series",
    question:"What is the next number in the series?\n2, 3, 5, 8, 13, 21, ___",
    options:["29","33","34","36"],
    answer:"34",
    explanation:"Fibonacci sequence: each number is the sum of the two before it. 13 + 21 = 34.",
  },
  {
    id:"NR11", section:"numerical", difficulty:4,
    type:"data",
    question:"Staff headcount: Year 1: 80, Year 2: 100, Year 3: 140, Year 4: 200. If this trend continues, what is the Year 5 headcount?",
    options:["240","260","280","300"],
    answer:"280",
    explanation:"Increases: +20, +40, +60, +80 (each increase grows by 20). Next increase = +80, so 200 + 80 = 280.",
  },
  {
    id:"NR12", section:"numerical", difficulty:4,
    type:"word_problem",
    question:"An investment of $10,000 grows at 10% compound interest annually. What is it worth after 3 years?",
    options:["$12,100","$13,000","$13,100","$13,310"],
    answer:"$13,310",
    explanation:"$10,000 × 1.1³ = $10,000 × 1.331 = $13,310.",
  },

  // Difficulty 5
  {
    id:"NR13", section:"numerical", difficulty:5,
    type:"data",
    question:"Department A has 40 staff with avg salary $52,000. Department B has 60 staff with avg salary $48,000. What is the combined average salary?",
    options:["$49,200","$49,600","$50,000","$50,400"],
    answer:"$49,600",
    explanation:"Total salary: (40×$52K) + (60×$48K) = $2,080K + $2,880K = $4,960K. Average = $4,960K ÷ 100 = $49,600.",
  },
  {
    id:"NR14", section:"numerical", difficulty:5,
    type:"word_problem",
    question:"A factory produces 1,200 units in 8 hours with 6 machines. How many units can 9 machines produce in 10 hours, assuming constant rate?",
    options:["1,800","2,000","2,200","2,250"],
    answer:"2,250",
    explanation:"Rate per machine per hour = 1,200 ÷ (8×6) = 25 units. 9 machines × 10 hours × 25 = 2,250.",
  },
  {
    id:"NR15", section:"numerical", difficulty:5,
    type:"series",
    question:"What is the missing number?\n144, 121, ___, 81, 64, 49",
    options:["96","100","102","108"],
    answer:"100",
    explanation:"Series of perfect squares in descending order: 12², 11², 10², 9², 8², 7². 10² = 100.",
  },

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 3: ABSTRACT REASONING (AR1–AR15)
  // Pattern recognition, sequences, odd-one-out, matrix reasoning
  // ════════════════════════════════════════════════════════════════════════════

  {
    id:"AR1", section:"abstract", difficulty:1,
    type:"pattern",
    question:"Which symbol completes the pattern?\n△ ○ △ ○ △ ___",
    options:["△","○","□","◇"],
    answer:"○",
    explanation:"The pattern alternates triangle, circle. After triangle, the next is circle.",
  },
  {
    id:"AR2", section:"abstract", difficulty:1,
    type:"series",
    question:"Shape sequence: 3 sides → 4 sides → 5 sides → 6 sides → ___",
    options:["6 sides","7 sides","8 sides","9 sides"],
    answer:"7 sides",
    explanation:"Each shape adds one side: triangle, square, pentagon, hexagon, heptagon.",
  },
  {
    id:"AR3", section:"abstract", difficulty:1,
    type:"odd_one_out",
    question:"Which letter sequence is the ODD ONE OUT?\nABD   CEG   DFH   EGI   FGJ",
    options:["ABD","CEG","DFH","EGI","FGJ"],
    answer:"FGJ",
    explanation:"ABD, CEG, DFH, EGI all skip one letter between each pair. FGJ skips one between G and J but not F and G.",
  },
  {
    id:"AR4", section:"abstract", difficulty:2,
    type:"pattern",
    question:"In a 3×3 grid, each row contains shapes ●, ■, ▲ in different orders. Row 1: ●■▲, Row 2: ■▲●, Row 3: ▲?●. What replaces '?'",
    options:["●","■","▲","◇"],
    answer:"■",
    explanation:"Each shape appears once per row and column. ▲ starts Row 3, ● ends it, so ■ must be in the middle.",
  },
  {
    id:"AR5", section:"abstract", difficulty:2,
    type:"series",
    question:"Letters: A, C, F, J, O, ___. What letter comes next?",
    options:["S","T","U","V"],
    answer:"U",
    explanation:"Gaps increase: +2, +3, +4, +5, +6. O + 6 = U.",
  },
  {
    id:"AR6", section:"abstract", difficulty:2,
    type:"pattern",
    question:"Each row: Large shape contains a small shape. Row 1: Large■[small●]. Row 2: Large▲[small■]. Row 3: Large●[?]",
    options:["small●","small▲","small■","small◇"],
    answer:"small▲",
    explanation:"The small shape follows the sequence: ●, ■, ▲ (cycling through the three shapes).",
  },
  {
    id:"AR7", section:"abstract", difficulty:3,
    type:"series",
    question:"Number-shape code: 1=●, 2=■, 3=▲. Sequence: ●●■, ●■▲, ■▲▲, ▲▲?",
    options:["▲","●","■","◇"],
    answer:"●",
    explanation:"Pattern: each triplet shifts right and the new element cycles 1→2→3→1. After ▲▲, the new element resets to ● (1).",
  },
  {
    id:"AR8", section:"abstract", difficulty:3,
    type:"matrix",
    question:"In a 3×3 matrix: each row has 1, 2, 3 dots. Each column has 1, 2, 3 dots. Row 3 has: [3 dots][1 dot][?]. What goes in the missing cell?",
    options:["1 dot","2 dots","3 dots","0 dots"],
    answer:"2 dots",
    explanation:"Column 3 already has 1 and 3, so it needs 2. Row 3 already has 3 and 1, so 2 completes the row.",
  },
  {
    id:"AR9", section:"abstract", difficulty:3,
    type:"odd_one_out",
    question:"Which group is the ODD ONE OUT?\nAZ, BY, CX, DW, FV",
    options:["AZ","BY","CX","DW","FV"],
    answer:"FV",
    explanation:"AZ=1+26, BY=2+25, CX=3+24, DW=4+23 (each pair sums to 27). FV=6+22=28, not 27. EV would be the pattern.",
  },
  {
    id:"AR10", section:"abstract", difficulty:4,
    type:"pattern",
    question:"Rule: shapes rotate 90° clockwise each step AND add one side per step. Start: ▲(3 sides). Step 2: □ rotated. Step 3: Pentagon rotated. Step 4 colour: all DARK. Step 5 colour: all LIGHT. Step 6 is: ?",
    options:["Dark heptagon","Light heptagon","Dark octagon","Light octagon"],
    answer:"Dark heptagon",
    explanation:"Step 5 = light hexagon. Step 6 adds one side (heptagon) and colour alternates back to dark.",
  },
  {
    id:"AR11", section:"abstract", difficulty:4,
    type:"series",
    question:"Code sequence: A1, B4, C9, D16, E25, ___",
    options:["F30","F36","G36","F35"],
    answer:"F36",
    explanation:"Letters increment A→F. Numbers are squares: 1,4,9,16,25,36. Next is F36.",
  },
  {
    id:"AR12", section:"abstract", difficulty:4,
    type:"matrix",
    question:"3×3 matrix rule: value = row × column. Row 1: [1][2][3]. Row 2: [2][4][6]. Row 3: [3][6][?]",
    options:["7","8","9","12"],
    answer:"9",
    explanation:"Row 3, Column 3 = 3 × 3 = 9.",
  },
  {
    id:"AR13", section:"abstract", difficulty:5,
    type:"pattern",
    question:"Mirror rule: each shape in the right column is the mirror of the left column shape MINUS one side. Left column: ■(4), ▲(3), Hexagon(6). Right column: ▲(3), ?(2), Pentagon(5). What is the missing shape?",
    options:["A line segment","A circle","A triangle","A square"],
    answer:"A line segment",
    explanation:"Right column shapes have one fewer side than their mirror. ▲ has 3 sides; 3-1=2 sides = a line segment.",
  },
  {
    id:"AR14", section:"abstract", difficulty:5,
    type:"series",
    question:"Binary-like sequence: 001, 010, 011, 100, 101, 110, ___",
    options:["111","120","200","011"],
    answer:"111",
    explanation:"Binary counting sequence: 1,2,3,4,5,6,7 in binary. 7 = 111.",
  },
  {
    id:"AR15", section:"abstract", difficulty:5,
    type:"matrix",
    question:"In a 4×4 Latin square, each row and column contains 1,2,3,4. Row 1:[1,2,3,4] Row 2:[2,1,4,3] Row 3:[3,4,1,2] Row 4:[4,?,2,1]. What replaces '?'",
    options:["1","2","3","4"],
    answer:"3",
    explanation:"Column 2 already has 2,1,4. The missing number in that column is 3.",
  },

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 4: LOGICAL REASONING (LR1–LR15)
  // Syllogisms, deduction, critical thinking, assumptions, conclusions
  // ════════════════════════════════════════════════════════════════════════════

  {
    id:"LR1", section:"logical", difficulty:1,
    type:"syllogism",
    question:"All projects have deadlines. Project Alpha is a project. Therefore:",
    options:[
      "Project Alpha has a deadline",
      "All deadlines belong to projects",
      "Project Alpha may have a deadline",
      "Project Alpha has no deadline",
    ],
    answer:"Project Alpha has a deadline",
    explanation:"If all projects have deadlines, and Project Alpha is a project, then Project Alpha definitely has a deadline.",
  },
  {
    id:"LR2", section:"logical", difficulty:1,
    type:"deduction",
    question:"If it rains, the meeting is cancelled. The meeting was not cancelled. What can we conclude?",
    options:[
      "It rained",
      "It did not rain",
      "The meeting was rescheduled",
      "Nothing can be concluded",
    ],
    answer:"It did not rain",
    explanation:"Contrapositive: if the meeting is NOT cancelled, then it did NOT rain.",
  },
  {
    id:"LR3", section:"logical", difficulty:1,
    type:"sequence",
    question:"Tom is taller than Sarah. Sarah is taller than Mike. Who is the shortest?",
    options:["Tom","Sarah","Mike","Cannot be determined"],
    answer:"Mike",
    explanation:"Tom > Sarah > Mike. Therefore Mike is the shortest.",
  },
  {
    id:"LR4", section:"logical", difficulty:2,
    type:"syllogism",
    question:"Some accountants are CFOs. All CFOs attend board meetings. Therefore:",
    options:[
      "All accountants attend board meetings",
      "Some accountants attend board meetings",
      "No accountants attend board meetings",
      "All board members are accountants",
    ],
    answer:"Some accountants attend board meetings",
    explanation:"Some accountants are CFOs, and all CFOs attend board meetings, so those accountants who are CFOs attend board meetings.",
  },
  {
    id:"LR5", section:"logical", difficulty:2,
    type:"critical",
    question:"'Our company should invest in AI because all our competitors are doing so.' What is the main weakness of this argument?",
    options:[
      "AI is too expensive",
      "It is an appeal to popularity, not evidence of benefit",
      "Competitors are always wrong",
      "The company cannot afford AI",
    ],
    answer:"It is an appeal to popularity, not evidence of benefit",
    explanation:"Doing something because competitors do it (bandwagon effect) is a logical fallacy — it doesn't establish that the action is beneficial.",
  },
  {
    id:"LR6", section:"logical", difficulty:2,
    type:"deduction",
    question:"All members of Team A completed training. Jane did not complete training. What follows?",
    options:[
      "Jane is not a member of Team A",
      "Jane may be a member of Team A",
      "Jane will join Team A",
      "Team A is incomplete",
    ],
    answer:"Jane is not a member of Team A",
    explanation:"Since all Team A members completed training, and Jane did not, Jane cannot be a member of Team A.",
  },
  {
    id:"LR7", section:"logical", difficulty:3,
    type:"assumption",
    question:"Statement: 'We should hire only candidates who graduated from top universities.' Which assumption underlies this statement?",
    options:[
      "Top universities are expensive",
      "Graduating from a top university predicts job performance",
      "All good candidates go to top universities",
      "Companies should reduce hiring costs",
    ],
    answer:"Graduating from a top university predicts job performance",
    explanation:"The statement assumes university prestige correlates with candidate quality/performance — otherwise, the criteria would be arbitrary.",
  },
  {
    id:"LR8", section:"logical", difficulty:3,
    type:"sequence",
    question:"A is 2 ranks above B. C is 1 rank below D. D is 3 ranks above B. What is the ranking from highest to lowest?",
    options:["D, A, C, B","A, D, C, B","D, C, A, B","A, C, D, B"],
    answer:"A, D, C, B",
    explanation:"B=1, A=3 (B+2), D=4 (B+3), C=3 (D-1). So: D(4), A=C(3), B(1). A and D both rank above B. D=4>A=3: D,A,C,B.",
  },
  {
    id:"LR9", section:"logical", difficulty:3,
    type:"critical",
    question:"'Sales increased 30% after we launched our new website. Therefore, the website caused the increase.' What logical error is made?",
    options:[
      "Confirmation bias",
      "Post hoc ergo propter hoc (correlation confused with causation)",
      "Straw man argument",
      "False dichotomy",
    ],
    answer:"Post hoc ergo propter hoc (correlation confused with causation)",
    explanation:"Assuming that because B followed A, A must have caused B — ignores other possible causes.",
  },
  {
    id:"LR10", section:"logical", difficulty:4,
    type:"syllogism",
    question:"No risk-averse manager supports disruptive innovation. Some board members are risk-averse managers. All board members make strategic decisions. Therefore:",
    options:[
      "Some strategic decision-makers don't support disruptive innovation",
      "All strategic decision-makers are risk-averse",
      "No board members support innovation",
      "All risk-averse managers are board members",
    ],
    answer:"Some strategic decision-makers don't support disruptive innovation",
    explanation:"Some board members are risk-averse managers who don't support disruptive innovation. Board members make strategic decisions. So some strategic decision-makers don't support disruptive innovation.",
  },
  {
    id:"LR11", section:"logical", difficulty:4,
    type:"deduction",
    question:"If the budget is approved, the project starts. If the project starts, the team is hired. The team was NOT hired. Which statement must be true?",
    options:[
      "The budget was approved but the project didn't start",
      "The budget was not approved",
      "The project started but the team wasn't hired",
      "The budget may or may not have been approved",
    ],
    answer:"The budget was not approved",
    explanation:"Chain: budget→project→team. Team NOT hired ⟹ project NOT started ⟹ budget NOT approved (contrapositive chain).",
  },
  {
    id:"LR12", section:"logical", difficulty:4,
    type:"critical",
    question:"'We've always done it this way, so we shouldn't change.' What is this fallacy called?",
    options:[
      "Slippery slope",
      "Appeal to tradition",
      "False analogy",
      "Ad hominem",
    ],
    answer:"Appeal to tradition",
    explanation:"Arguing something is right or best simply because it is traditional is the appeal to tradition fallacy.",
  },
  {
    id:"LR13", section:"logical", difficulty:5,
    type:"sequence",
    question:"Five candidates A–E are ranked. A is not 1st. B is higher than D. E is 3rd. C is 1st. B and D are adjacent. What is the correct ranking?",
    options:["C,B,E,D,A","C,A,E,B,D","C,B,E,A,D","C,D,E,B,A"],
    answer:"C,B,E,D,A",
    explanation:"C=1st (given). E=3rd (given). A≠1st. B>D and B,D adjacent. Positions 2,4,5 for A,B,D. B>D and adjacent: B=2,D=4 doesn't work (not adjacent). B=4,D=5: B>D fails. B=2,D=3 — but E=3. Therefore B=4,D=5: fails. Only B=2,D=3 fails due to E. Final: C,B,E,D,A with B=2,D=4 skipping over E.",
  },
  {
    id:"LR14", section:"logical", difficulty:5,
    type:"syllogism",
    question:"Only if a company is profitable can it pay dividends. XYZ paid dividends. ABC did not pay dividends. What can we conclude?",
    options:[
      "XYZ is profitable; ABC is not profitable",
      "XYZ is profitable; ABC may or may not be profitable",
      "Both XYZ and ABC are profitable",
      "Neither XYZ nor ABC is profitable",
    ],
    answer:"XYZ is profitable; ABC may or may not be profitable",
    explanation:"Paying dividends ⟹ profitable (XYZ paid, so XYZ is profitable). Not paying dividends does NOT necessarily mean unprofitable — ABC might be profitable but chose not to pay dividends.",
  },
  {
    id:"LR15", section:"logical", difficulty:5,
    type:"critical",
    question:"A study shows people who drink coffee are more productive. The researcher concludes: 'Drink coffee to be more productive.' What is the strongest objection to this conclusion?",
    options:[
      "Coffee is expensive",
      "Productive people may have more energy to drink coffee — causation may be reversed",
      "The study sample was too small",
      "Coffee is bad for health",
    ],
    answer:"Productive people may have more energy to drink coffee — causation may be reversed",
    explanation:"Correlation between coffee and productivity doesn't establish direction of causation. Productivity might lead to coffee drinking (reverse causation), or a third factor causes both.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SCORING ENGINE
// ─────────────────────────────────────────────────────────────────────────────

// Norm table: maps raw score (0–60) to IQ (70–140)
// Based on normal distribution mean=100, SD=15
// Raw score mean assumed = 30 (50%) → IQ 100
export function rawToIQ(rawScore, totalQuestions = 60) {
  const pct = rawScore / totalQuestions; // 0 to 1
  // Convert percentage to z-score using logistic approximation
  const z = 4.9 * (pct - 0.5); // maps 0–1 to roughly -2.45 to +2.45
  const iq = Math.round(100 + 15 * z);
  return Math.max(70, Math.min(140, iq));
}

export function sectionRawToIQ(correct, total = 15) {
  return rawToIQ(correct, total);
}

export function iqToPercentile(iq) {
  // Standard normal CDF approximation for IQ scores
  const z = (iq - 100) / 15;
  const percentile = 50 * (1 + erf(z / Math.sqrt(2)));
  return Math.round(Math.max(1, Math.min(99, percentile)));
}

function erf(x) {
  // Abramowitz and Stegun approximation
  const a = [0.0705230784, 0.0422820123, 0.0092705272,
              0.0001520143, 0.0002765672, 0.0000430638];
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - ((((((a[5]*t+a[4])*t)+a[3])*t+a[2])*t+a[1])*t+a[0])*t*Math.exp(-x*x);
  return x >= 0 ? y : -y;
}

export function getIQBand(iq) {
  if (iq >= 130) return { label:"Very Superior",   color:"#7c3aed", bg:"#f5f3ff", hire:"Exceptional — rare cognitive ability" };
  if (iq >= 120) return { label:"Superior",        color:"#0891b2", bg:"#cffafe", hire:"Highly recommended — top quartile" };
  if (iq >= 110) return { label:"High Average",    color:"#0f766e", bg:"#ccfbf1", hire:"Recommended — above average ability" };
  if (iq >= 90)  return { label:"Average",         color:"#475569", bg:"#f8fafc", hire:"Suitable — within normal range" };
  if (iq >= 80)  return { label:"Low Average",     color:"#d97706", bg:"#fef3c7", hire:"Review required — below average" };
  return               { label:"Below Average",   color:"#dc2626", bg:"#fef2f2", hire:"Not recommended for cognitively demanding roles" };
}

export function getHiringRecommendation(iq, role = "General") {
  const band = getIQBand(iq);
  const percentile = iqToPercentile(iq);
  return {
    band: band.label,
    recommendation: band.hire,
    suitable: iq >= 90,
    topQuartile: iq >= 110,
    percentile,
  };
}
